import { memo, useState, useEffect } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Typography, useMediaQuery } from '@mui/material';

// project imports
import getMenuItems from 'menu-items';
import NavGroup from './NavGroup';
import LAYOUT_CONST from 'constant';
import { HORIZONTAL_MAX_ITEM } from 'config';
import useConfig from 'hooks/useConfig';
import { fetchEvents, selectQicEventsData, selectQicYearOptions } from 'store/slices/qic';
import { useSelector } from 'store';

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const { layout } = useConfig();
    
    // State to trigger re-render when localStorage changes
    const [menuUpdateTrigger, setMenuUpdateTrigger] = useState(0);

    // Listen for localStorage changes
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'users' || e.key === null) {
                // Trigger re-render when user data changes
                setMenuUpdateTrigger(prev => prev + 1);
            }
        };

        // Listen for storage events (for cross-tab changes)
        window.addEventListener('storage', handleStorageChange);
        
        // Also listen for custom events (for same-tab changes)
        const handleCustomStorageChange = () => {
            setMenuUpdateTrigger(prev => prev + 1);
        };
        window.addEventListener('localStorageUpdated', handleCustomStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('localStorageUpdated', handleCustomStorageChange);
        };
    }, []);

    // QIC's sub-menu is its event years, which live in the database, so the menu
    // cannot be a module constant — the years have to reach getMenuItems. This
    // subscription is what re-renders the sidebar when they land.
    //
    // The fetch is the same one the in-page strip makes, gated the same way, so
    // it runs once per session and whichever of the two gets there first fills
    // it in for both. It fires for every user, not only on QIC pages, because
    // the sidebar shows QIC's years wherever you are — `GET /api/events`, once.
    //
    // CALL it, do not dispatch it: fetchEvents is async and dispatches its own
    // setEventsData through the slice's imported `dispatch`.
    const qicYears = useSelector(selectQicYearOptions);
    // The same fetch's event rows, for the `status` that says which year is the
    // running championship — the sidebar marks that one with a dot, as the strip
    // does. Read from the store rather than re-derived, for the same reason the
    // years are: one implementation of which year is live, not two.
    const qicEvents = useSelector(selectQicEventsData);
    useEffect(() => {
        if (qicYears.length === 0) fetchEvents();
    }, [qicYears.length]);

    // Get menu items dynamically whenever trigger changes
    const menuItem = getMenuItems(qicYears, qicEvents);

    // last menu-item to show in horizontal menu bar
    const lastItem = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd ? HORIZONTAL_MAX_ITEM : null;

    let lastItemIndex = menuItem.items.length - 1;
    let remItems = [];
    let lastItemId;

    if (lastItem && lastItem < menuItem.items.length) {
        lastItemId = menuItem.items[lastItem - 1].id;
        lastItemIndex = lastItem - 1;
        remItems = menuItem.items.slice(lastItem - 1, menuItem.items.length).map((item) => ({
            title: item.title,
            elements: item.children
        }));
    }

    // `isLast` lets NavGroup drop the divider that would otherwise trail the
    // final group — the flat sidebar has one divider, above Admin, not below it.
    const visibleItems = menuItem.items.slice(0, lastItemIndex + 1);

    const navItems = visibleItems.map((item, index) => {
        switch (item.type) {
            case 'group':
                return (
                    <NavGroup
                        key={`${item.id}-${menuUpdateTrigger}`}
                        item={item}
                        lastItem={lastItem}
                        remItems={remItems}
                        lastItemId={lastItemId}
                        isLast={index === visibleItems.length - 1}
                    />
                );
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    return <>{navItems}</>;
};

export default memo(MenuList);
